import { onBeforeUnmount, ref, watch } from 'vue';

// Tracks whether an element is within `rootMargin` of the viewport.
// When it leaves, we wait `unmountDelayMs` before flipping back to false —
// so that scrolling past a tile and back doesn't thrash mount/unmount.
// Set unmountDelayMs = Infinity for sticky "mount once" semantics.
export default function useVisibility(elementRef, {
  rootMargin = '300px',
  unmountDelayMs = Infinity,
} = {}) {
  const isVisible = ref(false);
  let observer = null;
  let unmountTimer = null;

  const clearUnmountTimer = () => {
    if (unmountTimer !== null) {
      clearTimeout(unmountTimer);
      unmountTimer = null;
    }
  };

  const disconnect = () => {
    clearUnmountTimer();
    if (observer !== null) {
      observer.disconnect();
      observer = null;
    }
  };

  const observe = (el) => {
    if (!el) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      isVisible.value = true;
      return;
    }

    observer = new IntersectionObserver((entries) => {
      const intersecting = entries.some((e) => e.isIntersecting);
      if (intersecting) {
        clearUnmountTimer();
        isVisible.value = true;
      } else if (isVisible.value && unmountDelayMs !== Infinity) {
        clearUnmountTimer();
        unmountTimer = setTimeout(() => {
          isVisible.value = false;
          unmountTimer = null;
        }, unmountDelayMs);
      }
    }, { rootMargin });
    observer.observe(el);
  };

  watch(elementRef, (el) => {
    disconnect();
    observe(el);
  }, { immediate: true, flush: 'post' });

  onBeforeUnmount(disconnect);

  return { isVisible };
}
